import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import QRPlacerCanvas from './QRPlacerCanvas';
import themes from '../../config/qrThemes';
import categories from '../../config/qrCategories';
import { toPng, toJpeg } from 'html-to-image';
import toast from 'react-hot-toast';

const ThemeSelectionModal = ({ 
  isOpen, 
  onClose, 
  tableData,
  restaurantData,
  qrCodeUrl 
}) => {
  const [selectedTheme, setSelectedTheme] = useState(themes[0]);
  const [activeMainCategory, setActiveMainCategory] = useState('english');
  const [activeSubcategory, setActiveSubcategory] = useState('fast-food');
  const [isDownloading, setIsDownloading] = useState(false);
  const [visualTheme, setVisualTheme] = useState('lime'); // 'lime', 'orange', or 'green'
  const posterRef = useRef(null);

  if (!isOpen) return null;

  // Get themes for active subcategory
  const getActiveSubcategoryData = () => {
    let subcategory;
    if (activeMainCategory === 'english') {
      subcategory = categories.english.subcategories.find(s => s.id === activeSubcategory);
    } else if (activeMainCategory === 'bangalore') {
      subcategory = categories.bangalore.subcategories.find(s => s.id === activeSubcategory);
    } else if (activeMainCategory === 'multilingual') {
      subcategory = categories.multilingual.subcategories.find(s => s.id === activeSubcategory);
    } else {
      subcategory = categories.ageWise.subcategories.find(s => s.id === activeSubcategory);
    }
    
    if (!subcategory) return { name: '', themes: [] };
    
    // Handle both category-based and themeIds-based subcategories
    const filteredThemes = 'category' in subcategory
      ? themes.filter(t => t.category === subcategory.category)
      : themes.filter(t => subcategory.themeIds.includes(t.id));
    
    return {
      name: subcategory.name,
      themes: filteredThemes
    };
  };

  const activeData = getActiveSubcategoryData();

  const handleDownload = async (format) => {
    if (!posterRef.current) {
      toast.error('QR placer not ready. Please try again.');
      return;
    }

    setIsDownloading(true);
    try {
      const node = posterRef.current;
      const themeName = selectedTheme.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const fileName = `ordyrr-qr-table-${tableData.table_number}-${visualTheme}-theme-${selectedTheme.id}-${themeName}.${format}`;
      
      // Options to avoid CORS issues with external fonts
      const options = {
        pixelRatio: 3,
        cacheBust: true,
        skipFonts: true, // Skip external fonts to avoid CORS errors
        backgroundColor: format === 'png' ? 'transparent' : '#ffffff', // Transparent for PNG, white for JPG
        ...(format === 'jpg' && { quality: 0.95 })
      };
      
      const dataUrl = format === 'png'
        ? await toPng(node, options)
        : await toJpeg(node, options);
      
      const link = document.createElement('a');
      link.download = fileName;
      link.href = dataUrl;
      link.click();
      
      toast.success(`QR placer downloaded as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Failed to export poster', error);
      toast.error('Failed to download QR placer. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="bg-white rounded-xl w-full max-w-6xl h-[90vh] flex flex-col shadow-2xl"
      >
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-neutral-200 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-neutral-900">
                Choose QR Placer Theme
              </h3>
              <p className="text-sm text-neutral-600 mt-1">
                Table {tableData.table_number} • {tableData.capacity} seats • {tableData.location}
              </p>
            </div>
            
            {/* Visual Theme Switcher */}
            <div className="flex items-center gap-3">
              <div className="flex gap-2">
                <button
                  onClick={() => setVisualTheme('lime')}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                    visualTheme === 'lime'
                      ? 'bg-[#c5ff4d] text-gray-900 shadow-lg border-2 border-gray-900'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-transparent'
                  }`}
                  title="Lime Theme - Ordyrr Branding"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <rect x="4" y="4" width="16" height="16" rx="8" />
                  </svg>
                  <span className="hidden sm:inline">Lime</span>
                </button>
                <button
                  onClick={() => setVisualTheme('orange')}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                    visualTheme === 'orange'
                      ? 'bg-[#FFA500] text-black shadow-lg border-2 border-black'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-transparent'
                  }`}
                  title="Orange Theme - Restaurant Branding"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <rect x="4" y="4" width="16" height="16" rx="2" />
                  </svg>
                  <span className="hidden sm:inline">Orange</span>
                </button>
                <button
                  onClick={() => setVisualTheme('green')}
                  className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                    visualTheme === 'green'
                      ? 'bg-[#00E676] text-gray-900 shadow-lg border-2 border-gray-900'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-transparent'
                  }`}
                  title="Green Theme - Customer Menu Style"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <circle cx="12" cy="12" r="8" />
                  </svg>
                  <span className="hidden sm:inline">Green</span>
                </button>
              </div>
              
              <button
                onClick={onClose}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex flex-col sm:flex-row">
          {/* Sidebar - Theme Categories */}
          <div className="w-full sm:w-80 border-b sm:border-b-0 sm:border-r border-neutral-200 overflow-y-auto">
            <div className="p-4 space-y-2">
              {/* Main Categories */}
              {/* English Version */}
              <div>
                <button
                  onClick={() => {
                    setActiveMainCategory('english');
                    setActiveSubcategory('fast-food');
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg font-bold text-sm transition-all ${
                    activeMainCategory === 'english'
                      ? 'bg-gray-900 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🌍 {categories.english.name}
                </button>
                
                {/* English Subcategories */}
                {activeMainCategory === 'english' && (
                  <div className="mt-2 ml-4 space-y-1">
                    {categories.english.subcategories.map(sub => {
                      const themeCount = themes.filter(t => t.category === sub.category).length;
                      return (
                        <button
                          key={sub.id}
                          onClick={() => setActiveSubcategory(sub.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                            activeSubcategory === sub.id
                              ? 'bg-[#c5ff4d] text-gray-900 font-semibold shadow-md'
                              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span>{sub.name}</span>
                            <span className="text-[10px] opacity-60">{themeCount}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Bangalore Version */}
              <div className="pt-2">
                <button
                  onClick={() => {
                    setActiveMainCategory('bangalore');
                    setActiveSubcategory('blr-tech-parks');
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg font-bold text-sm transition-all ${
                    activeMainCategory === 'bangalore'
                      ? 'bg-gray-900 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🏙️ {categories.bangalore.name}
                </button>
                
                {activeMainCategory === 'bangalore' && (
                  <div className="mt-2 ml-4 space-y-1">
                    {categories.bangalore.subcategories.map(sub => {
                      const themeCount = 'themeIds' in sub ? sub.themeIds.length : 0;
                      return (
                        <button
                          key={sub.id}
                          onClick={() => setActiveSubcategory(sub.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                            activeSubcategory === sub.id
                              ? 'bg-[#c5ff4d] text-gray-900 font-semibold shadow-md'
                              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span>{sub.name}</span>
                            <span className="text-[10px] opacity-60">{themeCount}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Multilingual Version */}
              <div className="pt-2">
                <button
                  onClick={() => {
                    setActiveMainCategory('multilingual');
                    setActiveSubcategory('tamil-all');
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg font-bold text-sm transition-all ${
                    activeMainCategory === 'multilingual'
                      ? 'bg-gray-900 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🌏 {categories.multilingual.name}
                </button>
                
                {activeMainCategory === 'multilingual' && (
                  <div className="mt-2 ml-4 space-y-1">
                    {categories.multilingual.subcategories.map(sub => {
                      const themeCount = 'category' in sub 
                        ? themes.filter(t => t.category === sub.category).length 
                        : sub.themeIds.length;
                      return (
                        <button
                          key={sub.id}
                          onClick={() => setActiveSubcategory(sub.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                            activeSubcategory === sub.id
                              ? 'bg-[#c5ff4d] text-gray-900 font-semibold shadow-md'
                              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span>{sub.name}</span>
                            <span className="text-[10px] opacity-60">{themeCount}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Age-Wise Themes */}
              <div className="pt-2">
                <button
                  onClick={() => {
                    setActiveMainCategory('ageWise');
                    setActiveSubcategory('age-tamil-youth');
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg font-bold text-sm transition-all ${
                    activeMainCategory === 'ageWise'
                      ? 'bg-gray-900 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  👥 {categories.ageWise.name}
                </button>
                
                {activeMainCategory === 'ageWise' && (
                  <div className="mt-2 ml-4 space-y-1">
                    {categories.ageWise.subcategories.map(sub => {
                      const themeCount = 'themeIds' in sub ? sub.themeIds.length : 0;
                      return (
                        <button
                          key={sub.id}
                          onClick={() => setActiveSubcategory(sub.id)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                            activeSubcategory === sub.id
                              ? 'bg-[#c5ff4d] text-gray-900 font-semibold shadow-md'
                              : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-center">
                            <span>{sub.name}</span>
                            <span className="text-[10px] opacity-60">{themeCount}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Theme List */}
            <div className="border-t border-gray-200 p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase mb-2">
                {activeData.name} ({activeData.themes.length})
              </h3>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {activeData.themes.map(theme => (
                  <button
                    key={theme.id}
                    onClick={() => setSelectedTheme(theme)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-all ${
                      selectedTheme.id === theme.id
                        ? 'bg-gray-900 text-white font-semibold'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-medium">#{theme.id}: {theme.name}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Preview Section */}
          <div className="flex-1 overflow-y-auto">
            <div className="min-h-full p-4 sm:p-6 flex flex-col items-center justify-start">
              <div className="w-full max-w-md mx-auto flex flex-col items-center">
                <QRPlacerCanvas
                  qrCodeData={qrCodeUrl}
                  theme={selectedTheme}
                  tableNumber={tableData.table_number}
                  restaurantName={restaurantData?.name}
                  onQRReady={(ref) => { posterRef.current = ref; }}
                  visualTheme={visualTheme}
                />

                {/* Theme Info */}
                <div className="mt-6 mb-12 text-center text-sm text-gray-600">
                  <p className="font-bold text-gray-900">Current Theme:</p>
                  <p>#{selectedTheme.id} - {selectedTheme.name}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer - Download Actions */}
        <div className="p-4 sm:p-6 border-t border-neutral-200 bg-neutral-50 flex-shrink-0">
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-neutral-600 border border-neutral-300 rounded-lg hover:bg-neutral-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDownload('png')}
              disabled={isDownloading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {isDownloading ? 'Downloading...' : 'Download PNG'}
            </button>
            <button
              onClick={() => handleDownload('jpg')}
              disabled={isDownloading}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-white text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
            >
              <ArrowDownTrayIcon className="h-4 w-4" />
              {isDownloading ? 'Downloading...' : 'Download JPG'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ThemeSelectionModal;
